import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Group, Modal, Select, Stack, Switch, Table, Text, TextInput, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { getVisit, listReports, transitionVisitStatus, updateVisitData } from '../../api/resources';
import { useAuth } from '../../auth/auth-context';
import { flattenReportData, humanizeKey } from '../../lib/reportFields';
import { reportLabel, reportSubmissionLabel } from '../../lib/reportLabel';
import { VISIT_ASSIGNABLE_STATUSES, VisitStatus } from '../../types/domain';
import { CopyableId } from '../CopyableText';
import { EntityRef } from '../EntityRef';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { AssignVisitSection } from '../visits/AssignVisitSection';
import { RejectVisitModal } from '../visits/RejectVisitModal';
import { DetailField, DetailFieldGrid } from './DetailField';
import { DetailSection } from './DetailSection';
import { ReportPhotosSection } from './ReportPhotosSection';

export function VisitDetailPanel({ visitId }: { visitId: string }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { isAdmin } = useAuth();
    const [rejectOpen, setRejectOpen] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    const [qaAnalysis, setQaAnalysis] = useState<string | null>(null);
    const [approvalNotes, setApprovalNotes] = useState('');
    const [instructions, setInstructions] = useState('');
    const [flagged, setFlagged] = useState(false);
    const [flagReason, setFlagReason] = useState('');
    const [flagNotes, setFlagNotes] = useState('');
    const lookups = useAdminLookups({ includeCases: true });

    const visitQ = useQuery({
        queryKey: ['visit', visitId],
        queryFn: () => getVisit(visitId),
    });

    const reportsQ = useQuery({
        queryKey: ['reports', 'for-visit', visitId],
        queryFn: async () => {
            const result = await listReports({ page: 1, limit: 50, visitId });
            return result.data;
        },
    });

    const approveMutation = useMutation({
        mutationFn: async () => {
            // Persist QA findings before transitioning status.
            const qaData: { qaAnalysis?: string; approvalNotes?: string } = {};
            if (qaAnalysis) qaData.qaAnalysis = qaAnalysis;
            if (approvalNotes.trim()) qaData.approvalNotes = approvalNotes.trim();
            if (qaData.qaAnalysis || qaData.approvalNotes) {
                await updateVisitData(visitId, qaData);
            }
            return transitionVisitStatus(visitId, { toStatus: VisitStatus.Approved });
        },
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.visits.approved') });
            setApproveOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
            void queryClient.invalidateQueries({ queryKey: ['visits'] });
            void queryClient.invalidateQueries({ queryKey: ['qa-queue'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const updateDataMutation = useMutation({
        mutationFn: () =>
            updateVisitData(visitId, {
                instructions,
                flagged,
                flagReason: flagged ? flagReason.trim() || undefined : undefined,
                flagDetails: flagged && flagNotes.trim() ? { notes: flagNotes.trim() } : undefined,
            }),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.visits.dataUpdated') });
            void queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
            void queryClient.invalidateQueries({ queryKey: ['visits'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const clearFlagMutation = useMutation({
        mutationFn: () => updateVisitData(visitId, { flagged: false }),
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.visits.flagCleared') });
            void queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
            void queryClient.invalidateQueries({ queryKey: ['visits'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const visit = visitQ.data;
    const reports = reportsQ.data ?? [];
    const reportIds = reports.map((r) => r.id);

    useEffect(() => {
        if (!visit?.data) {
            return;
        }
        setInstructions(visit.data.instructions ?? '');
        setFlagged(Boolean(visit.data.flagged));
        setFlagReason(visit.data.flagReason ?? '');
        const notes = visit.data.flagDetails?.notes;
        setFlagNotes(typeof notes === 'string' ? notes : '');
        // Load persisted QA findings (qaAnalysis + approvalNotes).
        if (visit.data.qaAnalysis) setQaAnalysis(visit.data.qaAnalysis);
        if (visit.data.approvalNotes) setApprovalNotes(visit.data.approvalNotes);
    }, [visit?.data, visit?.id]);

    if (!visit) {
        return <Text c="dimmed">{t('common.loading.visit')}</Text>;
    }

    const caseRow = visit.caseRecord;
    const caseInfo = lookups.resolveCase(visit.caseId, caseRow);
    const org = lookups.resolveOrganization(caseRow?.organizationId, caseRow?.organization);
    const officer = lookups.resolveUser(visit.fieldOfficerId, visit.fieldOfficer);
    const caseSubjects = caseRow?.caseSubjects ?? [];
    const casePois = caseRow?.casePois ?? [];
    const visitPois = visit.visitPois ?? [];
    const visitData = visit.data;
    const mismatches = visitData?.flagDetails?.mismatches ?? [];
    const empty = t('common.empty');

    return (
        <Stack gap="lg">
            <DetailFieldGrid>
                <DetailField
                    label={t('fields.organization')}
                    value={
                        <EntityRef
                            title={org.title}
                            id={org.id}
                            href={org.id ? `/organizations?id=${org.id}` : undefined}
                            hideId={!org.id}
                        />
                    }
                />
                <DetailField
                    label={t('fields.case')}
                    value={
                        <EntityRef
                            title={caseInfo.title}
                            id={visit.caseId}
                            href={`/cases/${visit.caseId}`}
                        />
                    }
                />
                <DetailField label={t('fields.visitType')} value={visit.visitType ?? empty} />
                <DetailField
                    label={t('fields.status')}
                    value={
                        <Group gap="xs">
                            {visit.status ? (
                                <Badge variant="light">{visit.status}</Badge>
                            ) : (
                                empty
                            )}
                            {visitData?.flagged ? (
                                <Badge color="orange" variant="filled">
                                    {t('features.visits.flaggedBadge')}
                                </Badge>
                            ) : null}
                        </Group>
                    }
                />
                <DetailField
                    label={t('fields.fieldOfficer')}
                    value={
                        <EntityRef
                            title={officer.title}
                            id={officer.id}
                            secondary={officer.secondary}
                            hideId={!visit.fieldOfficerId}
                        />
                    }
                />
                <DetailField label={t('fields.visitId')} value={<CopyableId id={visit.id} />} />
                <DetailField
                    label={t('fields.startedAt')}
                    value={
                        visit.startedAt ? new Date(visit.startedAt).toLocaleString() : empty
                    }
                />
                <DetailField
                    label={t('fields.completedAt')}
                    value={
                        visit.completedAt ? new Date(visit.completedAt).toLocaleString() : empty
                    }
                />
                <DetailField
                    label={t('fields.lastVerified')}
                    value={
                        visit.completedAt ? new Date(visit.completedAt).toLocaleString() : empty
                    }
                />
                <DetailField
                    label={t('fields.instructions')}
                    value={visitData?.instructions?.trim() ? visitData.instructions : empty}
                />
                {visitData?.flagged ? (
                    <>
                        <DetailField
                            label={t('fields.flagReason')}
                            value={visitData.flagReason ?? empty}
                        />
                        {typeof visitData.flagDetails?.notes === 'string' ? (
                            <DetailField
                                label={t('fields.flagNotes')}
                                value={visitData.flagDetails.notes}
                            />
                        ) : null}
                    </>
                ) : null}
            </DetailFieldGrid>

            {visitData?.flagged && mismatches.length > 0 ? (
                <DetailSection title={t('features.visits.preverificationMismatches')}>
                    <Table withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.field')}</Table.Th>
                                <Table.Th>{t('features.visits.subjectValue')}</Table.Th>
                                <Table.Th>{t('features.visits.reportValue')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {mismatches.map((row) => (
                                <Table.Tr key={row.key}>
                                    <Table.Td>{row.key}</Table.Td>
                                    <Table.Td>{String(row.subjectValue ?? empty)}</Table.Td>
                                    <Table.Td>{String(row.reportValue ?? empty)}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </DetailSection>
            ) : null}

            {/* Instructions are pre-work guidance; only editable before submission. */}
            {isAdmin && (!visit.status || VISIT_ASSIGNABLE_STATUSES.has(visit.status)) ? (
                <DetailSection title={t('features.visits.editDataTitle')}>
                    <Stack gap="sm">
                        <Textarea
                            label={t('fields.instructions')}
                            minRows={2}
                            value={instructions}
                            onChange={(e) => setInstructions(e.currentTarget.value)}
                        />
                        <Switch
                            label={t('features.visits.flaggedLabel')}
                            checked={flagged}
                            onChange={(e) => setFlagged(e.currentTarget.checked)}
                        />
                        {flagged ? (
                            <>
                                <TextInput
                                    label={t('fields.flagReason')}
                                    value={flagReason}
                                    onChange={(e) => setFlagReason(e.currentTarget.value)}
                                />
                                <Textarea
                                    label={t('fields.flagNotes')}
                                    minRows={2}
                                    value={flagNotes}
                                    onChange={(e) => setFlagNotes(e.currentTarget.value)}
                                />
                            </>
                        ) : null}
                        <Group>
                            <Button
                                loading={updateDataMutation.isPending}
                                onClick={() => updateDataMutation.mutate()}
                            >
                                {t('common.actions.save')}
                            </Button>
                            {visitData?.flagged ? (
                                <Button
                                    variant="light"
                                    color="gray"
                                    loading={clearFlagMutation.isPending}
                                    onClick={() => clearFlagMutation.mutate()}
                                >
                                    {t('features.visits.clearFlag')}
                                </Button>
                            ) : null}
                        </Group>
                    </Stack>
                </DetailSection>
            ) : null}

            <DetailSection title={t('detail.subjectsOnCase')}>
                {caseSubjects.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        {t('detail.noSubjectOnVisit')}
                    </Text>
                ) : (
                    <Table withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.subject')}</Table.Th>
                                <Table.Th>{t('fields.role')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {caseSubjects.map((link) => (
                                <Table.Tr key={link.id}>
                                    <Table.Td>
                                        <EntityRef
                                            title={
                                                link.subject?.fullName ??
                                                link.subjectId ??
                                                t('entities.subject')
                                            }
                                            id={link.subjectId}
                                            href={`/subjects?id=${link.subjectId}`}
                                            hideId
                                        />
                                    </Table.Td>
                                    <Table.Td>{link.role ?? empty}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </DetailSection>

            <DetailSection title={t('detail.poisOnVisit')}>
                {casePois.length === 0 && visitPois.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        {t('detail.noPoiOnVisit')}
                    </Text>
                ) : (
                    <Table withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.poi')}</Table.Th>
                                <Table.Th>{t('fields.role')}</Table.Th>
                                <Table.Th>{t('fields.source')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {casePois.map((link) => (
                                <Table.Tr key={`case-${link.id}`}>
                                    <Table.Td>
                                        <EntityRef
                                            title={link.poi?.name ?? link.poiId ?? t('entities.poi')}
                                            id={link.poiId}
                                            secondary={link.poi?.claimedAddress}
                                            href={`/pois?id=${link.poiId}`}
                                            hideId
                                        />
                                    </Table.Td>
                                    <Table.Td>{link.role ?? empty}</Table.Td>
                                    <Table.Td>{t('detail.poiSourceCase')}</Table.Td>
                                </Table.Tr>
                            ))}
                            {visitPois.map((link) => (
                                <Table.Tr key={`visit-${link.id}`}>
                                    <Table.Td>
                                        <EntityRef
                                            title={link.poi?.name ?? link.poiId ?? t('entities.poi')}
                                            id={link.poiId}
                                            secondary={link.poi?.claimedAddress}
                                            href={`/pois?id=${link.poiId}`}
                                            hideId
                                        />
                                    </Table.Td>
                                    <Table.Td>{link.verificationScope ?? empty}</Table.Td>
                                    <Table.Td>{t('detail.poiSourceVisit')}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </DetailSection>

            <AssignVisitSection
                visitId={visitId}
                visitStatus={visit.status}
                currentFieldOfficerId={visit.fieldOfficerId}
                dispatchOrganizationId={
                    caseRow?.executionOrganizationId ?? caseRow?.organizationId
                }
            />

            <DetailSection title={t('detail.reportsOnVisit', { count: reports.length })}>
                {reports.length === 0 ? (
                    <Text size="sm" c="dimmed">
                        {t('detail.noReportsOnVisit')}
                    </Text>
                ) : (
                    <Table withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('fields.report')}</Table.Th>
                                <Table.Th>{t('fields.workflowType')}</Table.Th>
                                <Table.Th>{t('fields.submitted')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {reports.map((r) => (
                                <Table.Tr key={r.id}>
                                    <Table.Td>
                                        <EntityRef
                                            title={reportLabel(r)}
                                            id={r.id}
                                            href={`/reports?id=${r.id}`}
                                        />
                                    </Table.Td>
                                    <Table.Td>{r.workflowType ?? empty}</Table.Td>
                                    <Table.Td>{reportSubmissionLabel(r)}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )}
            </DetailSection>

            {/* What the field officer actually answered. `data` is free-form
                JSONB, so each top-level group renders as its own field grid. */}
            {reports.map((r) => {
                const groups = Object.entries(r.data ?? {});
                return (
                    <DetailSection
                        key={r.id}
                        title={t('detail.reportData')}
                        description={reportLabel(r)}
                    >
                        {groups.length === 0 ? (
                            <Text size="sm" c="dimmed">
                                {t('detail.noReportData')}
                            </Text>
                        ) : (
                            <Stack gap="md">
                                {groups.map(([group, value]) => {
                                    const fields = flattenReportData(value).filter(
                                        (f) => f.value !== '—',
                                    );
                                    if (fields.length === 0) {
                                        return null;
                                    }
                                    return (
                                        <Stack key={group} gap={4}>
                                            <Text size="sm" fw={600}>
                                                {humanizeKey(group)}
                                            </Text>
                                            <DetailFieldGrid>
                                                {fields.map((f, i) => (
                                                    <DetailField
                                                        key={`${group}-${f.label}-${i}`}
                                                        label={f.label}
                                                        value={f.value}
                                                    />
                                                ))}
                                            </DetailFieldGrid>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}
                    </DetailSection>
                );
            })}

            {/* Report photos — fetched with presigned downloadUrls. */}
            <ReportPhotosSection reportIds={reportIds} />

            {/* Approve / Reject are only offered while a decision is pending. */}
            {isAdmin && visit.status === VisitStatus.PendingApproval ? (
                <DetailSection
                    title={t('features.visits.approvalDecision')}
                    description={t('features.visits.approvalDecisionHint')}
                >
                    <Stack gap="sm">
                        <Select
                            label={t('features.visits.materialChange')}
                            data={[
                                {
                                    value: 'no_material_change',
                                    label: t('features.visits.materialChangeNone'),
                                },
                                {
                                    value: 'material_change_identified',
                                    label: t('features.visits.materialChangeIdentified'),
                                },
                            ]}
                            value={qaAnalysis}
                            onChange={setQaAnalysis}
                            maw={360}
                        />
                        <Textarea
                            label={t('features.visits.approvalNotesLabel')}
                            description={t('features.visits.approvalNotesHint')}
                            minRows={2}
                            value={approvalNotes}
                            onChange={(e) => setApprovalNotes(e.currentTarget.value)}
                        />
                        <Group grow>
                            <Button
                                color="green"
                                size="md"
                                leftSection={<IconCheck size={18} />}
                                onClick={() => setApproveOpen(true)}
                            >
                                {t('features.visits.approveAction')}
                            </Button>
                            <Button
                                color="red"
                                variant="light"
                                size="md"
                                leftSection={<IconX size={18} />}
                                onClick={() => setRejectOpen(true)}
                            >
                                {t('features.visits.rejectAction')}
                            </Button>
                        </Group>
                    </Stack>
                </DetailSection>
            ) : null}

            {/* Show saved QA findings when visit is approved. */}
            {visit.status === VisitStatus.Approved &&
            (visitData?.qaAnalysis || visitData?.approvalNotes) ? (
                <DetailSection title={t('features.visits.approvalDecision')}>
                    <Stack gap="xs">
                        {visitData?.qaAnalysis ? (
                            <DetailField
                                label={t('features.visits.materialChange')}
                                value={visitData.qaAnalysis}
                            />
                        ) : null}
                        {visitData?.approvalNotes ? (
                            <DetailField
                                label={t('features.visits.approvalNotesLabel')}
                                value={visitData.approvalNotes}
                            />
                        ) : null}
                    </Stack>
                </DetailSection>
            ) : null}

            {/* Mounted only while open so each decision starts from a clean form. */}
            {rejectOpen ? (
                <RejectVisitModal
                    opened
                    onClose={() => setRejectOpen(false)}
                    visitId={visitId}
                    caseId={visit.caseId}
                    visitType={visit.visitType}
                    currentOfficerId={visit.fieldOfficerId}
                    poiId={visit.visitPois?.[0]?.poiId}
                />
            ) : null}

            <Modal
                opened={approveOpen}
                onClose={() => setApproveOpen(false)}
                title={t('features.visits.approveTitle')}
            >
                <Stack gap="md">
                    <Text size="sm">{t('features.visits.approveConfirm')}</Text>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => setApproveOpen(false)}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button
                            color="green"
                            loading={approveMutation.isPending}
                            onClick={() => approveMutation.mutate()}
                        >
                            {t('features.visits.approveAction')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
}
